import { useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { rereadCustomDictionaryInSession } from '../../generic/utilities/languageCatalogue'
import { rejectionMessageKeyFor, isFileUnavailable } from './termsRejectionMap'
import { declaredPlaceholdersOf } from './placeholders'
import { impactOf, needsConfirmation } from './impact'
import { NO_SYSTEM_VALUES } from './systemValueTerms'

const PLACEHOLDERS_KEY = 'The term must keep these markers: {{markers}}'

/**
 * Historia 176 — B1. The single request flow every write path of the language texts tab shares
 * (`TermRow` for a row, `LanguageHistoryDialog` for "Language history"): RN9 confirmation
 * gate, the request itself, the rejection map by `code` (S121, S122), the 409 that puts the whole
 * tab read-only (S153) and CU11/RN32 (re-read the session dictionary when `change` is not null).
 *
 * Story 204: `systemValues` (`indexSystemValues` of C5) makes a system value always ask for the global confirmation
 * (RN6.4) and its placeholder rejection name the catalogue's params (RN6.5).
 *
 * @param {{code: string, artifact: object, systemValues: object, onApplied: (termKey: string, result: object) => void, onFileUnavailable: () => void}} options
 */
export const useTermRequestFlow = ({
  code = '',
  artifact = {},
  systemValues = NO_SYSTEM_VALUES,
  onApplied = () => {},
  onFileUnavailable = () => {}
}) => {
  const { t_, i18n } = useSmartTranslation()
  const [errorKey, setErrorKey] = useState('')
  const [errorTermKey, setErrorTermKey] = useState('')
  const [pending, setPending] = useState({ termKey: '', requestFn: undefined, change: { from: '', to: '' } })
  const [confirmOpen, setConfirmOpen] = useState(false)
  // RN6l/RN29: true while a request of this flow is in flight.
  const [pendingRequest, setPendingRequest] = useState(false)

  const run = async (termKey, requestFn) => {
    setErrorKey('')
    setErrorTermKey(termKey)
    setPendingRequest(true)
    try {
      const result = await requestFn()
      onApplied(termKey, result)
      // CU11/RN32: a change to the language the editor's own session runs on repaints without reload.
      if (result.change !== null) {
        await rereadCustomDictionaryInSession(i18n, code)
      }
    } catch (error) {
      if (isFileUnavailable(error)) {
        onFileUnavailable()
        return
      }
      setErrorKey(rejectionMessageKeyFor(error))
    } finally {
      setPendingRequest(false)
    }
  }

  // RN9: gate a save/delete/restore that needs confirmation (2+ modules, or common structure).
  // `change` is what the confirmation title says (RN9b, T59): `from` = what is painted today, `to` = what will be.
  const submit = (termKey, requestFn, change = { from: '', to: '' }) => {
    if (needsConfirmation(impactOf(termKey, artifact, systemValues))) {
      setPending({ termKey, requestFn, change })
      setConfirmOpen(true)
      return
    }
    run(termKey, requestFn)
  }

  const closeConfirmation = () => {
    setConfirmOpen(false)
    setPending({ termKey: '', requestFn: undefined, change: { from: '', to: '' } })
  }

  const handleConfirm = () => {
    const { termKey, requestFn } = pending
    closeConfirmation()
    run(termKey, requestFn)
  }

  // RN14c: the markers Jupiter shows are computed from the KEY, never from the error response; for a system value,
  // from the params its catalogue entry declares (story 204, RN6.5).
  const errorMessage = errorKey === PLACEHOLDERS_KEY
    ? t_(errorKey, { markers: declaredPlaceholdersOf(errorTermKey, systemValues).join(', ') })
    : t_(errorKey)

  return {
    errorKey,
    errorMessage,
    confirmOpen,
    pendingRequest,
    pendingTermKey: pending.termKey,
    pendingChange: pending.change,
    submit,
    handleConfirm,
    handleCancel: closeConfirmation
  }
}
